import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './CompanyFeatures.module.scss';
import VormatsAPI from '../../../../../../../common/apis/VormatsAPI';
import Spinner from '../../../../../../../common/components/Spinner';
import cx from 'classnames';
import usePagination from '../../../../../../hooks/usePagination';
import Pagination from '../../../../../../components/Pagination/Pagination';
import SearchComponent from '../../../../../../components/SearchComponent/SearchComponent';
import EditFeatureModal from '../EditFeatureModal/EditFeatureModal';
import { useTranslation } from 'react-i18next';
import Toggle from '../../../../../../components/Toggle/Toggle';

const CompanyFeatures = ({ companyId, companyName }) => {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState(null);

    const { t } = useTranslation();

    const getCompanyFeaturesList = useCallback(async params => {
        return await VormatsAPI.getCompanyFeatures(companyId, params);
    }, [companyId]);

    const {
        list: companyFeaturesList,
        query,
        totalData,
        isLoading,
        onSearch,
        onChangePage,
        loadList
    } = usePagination({
        service: getCompanyFeaturesList,
        itemsPerPage: 5,
        sortByFiled: 'id',
    });

    const scrollableContentRef = useRef(null);

    const isListScrollable = useMemo(() => {
        const { scrollHeight, offsetHeight } = scrollableContentRef?.current || {};
        return scrollHeight - offsetHeight > 0;
    }, [companyFeaturesList]);

    useEffect(() => {
        loadList();
    }, [companyId]);

    const handleOpenEditModal = (feature) => {
        setSelectedFeature({ ...feature, featureId: feature.featureId });
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedFeature(null);
    };

    const handleSaveEdit = async (updatedFeature) => {
        await VormatsAPI.updateCompanyFeature(companyId, updatedFeature);
        handleCloseEditModal();
        loadList();
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.headerContainer}>
                <h2 className={styles.header}>
                    {companyName ? `${companyName} ${t('company.settings.features.title')}` : t('company.settings.features.company.title')} ({totalData || 0})
                </h2>
                <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
            </div>
            <div className={styles.featuresTable}>
                <div className={styles.featuresTableHeader}>
                    <div className={styles.itemHeader}>
                        <span>{t('company.settings.features.name')}</span>
                    </div>
                </div>
                {isLoading ? (
                    <div className={styles.spinnerWrapper}>
                        <Spinner size="3px" width="40px" height="40px" />
                    </div>
                ) : (
                    <ul ref={scrollableContentRef} className={cx(styles.featureList, { [styles.scrollableFeatureList]: isListScrollable })}>
                        {companyFeaturesList.map(companyFeature => (
                            <li key={companyFeature.featureId} className={styles.featureItem} onClick={() => handleOpenEditModal(companyFeature)}>
                                <span className={styles.featureName}>{companyFeature.feature?.publicName || companyFeature.feature?.name || companyFeature.featureId}</span>
                                <Toggle
                                    id={`companyFeature-${companyFeature.featureId}`}
                                    checked={!!companyFeature.enabled}
                                    onClick={e => e.stopPropagation()}
                                    onChange={async (e) => {
                                        e.stopPropagation();
                                        const updatedFeature = { ...companyFeature, enabled: companyFeature.enabled ? 0 : 1 };
                                        await VormatsAPI.updateCompanyFeature(companyId, updatedFeature);
                                        loadList();
                                    }}
                                    className={styles.toggle}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className={styles.footerContainer}>
                {!!totalData && (
                    <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={onChangePage} />
                )}
            </div>
            <EditFeatureModal
                open={editModalOpen}
                featureData={selectedFeature}
                onClose={handleCloseEditModal}
                onSave={handleSaveEdit}
                isCreateMode={false}
                hideRename
            />
        </div>
    );
};

export default CompanyFeatures; 